import React, { memo, Suspense, useState } from 'react';
import { Button, Dropdown, message } from 'antd';
import { downloadAll } from '@helpers/download';
import {
  QUERY_ASSET_REVIEW_CONTENT,
  useUpdateAssetReviewMutation
} from '@hooks/queries/assetReview';
import { AssetItemDto } from '@api/Api';
import ConfirmModal from '@components/Modals/ConfirmModal';
import {
  ActionIcon,
  DeleteIcon,
  DownloadInvoiceIcon,
  SuccessIcon
} from '@icons';

type ActionFolderProps = {
  canManageReviews: boolean;
  assets: AssetItemDto[];
  folderName: string;
  reviewId: string;
  folderId: string;
};

export default memo(function ActionFolder({
  canManageReviews,
  assets,
  folderName,
  reviewId,
  folderId
}: ActionFolderProps) {
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
  const hideDownloadButton = !assets?.length;

  const { mutateAsync: updateAssetReview, isPending: isUpdatingAssetReview } =
    useUpdateAssetReviewMutation({
      queryKey: [QUERY_ASSET_REVIEW_CONTENT]
    });

  const onRemoveFolderFromReview = async () => {
    try {
      setIsShowConfirmModal(false);
      await updateAssetReview({
        reviewId,
        folders: {
          remove: [folderId]
        }
      });
      message.success({
        key: 'asset-removed-from-review',
        content: `${folderName} has been removed from the shared link.`,
        className: 'message-dark-modal',
        icon: <SuccessIcon className="anticon" />,
        duration: 2
      });
    } catch (error) {
      console.error(error);
    }
  };

  if (canManageReviews) {
    return (
      <>
        <Dropdown
          menu={{
            onClick: ({ domEvent }) => domEvent.stopPropagation(),
            items: [
              ...(hideDownloadButton
                ? []
                : [
                    {
                      key: 'download',
                      icon: <DownloadInvoiceIcon />,
                      label: 'Download',
                      onClick: () => {
                        if (!assets?.length) return;
                        downloadAll(assets, `${folderName}.zip`, folderId);
                      }
                    }
                  ]),
              {
                key: 'delete',
                icon: <DeleteIcon />,
                label: 'Remove from shared link',
                onClick: () => {
                  setIsShowConfirmModal(true);
                }
              }
            ]
          }}
          overlayClassName="asset_links_overlay"
          placement="bottomLeft"
          trigger={['click']}
        >
          <Button
            className="asset_links_action"
            onClick={(e) => e.stopPropagation()}
            type="text"
            size="small"
            loading={isUpdatingAssetReview}
            style={{
              width: 24,
              height: 24,
              marginLeft: 'auto'
            }}
          >
            <ActionIcon />
          </Button>
        </Dropdown>
        <Suspense fallback={null}>
          <div onClick={(e) => e.stopPropagation()}>
            <ConfirmModal
              visible={isShowConfirmModal}
              onConfirm={onRemoveFolderFromReview}
              onCancel={() => setIsShowConfirmModal(false)}
              okText="Remove"
              title="Remove folder from shared link"
              description="Are you sure you want to remove this folder from the shared link?"
            />
          </div>
        </Suspense>
      </>
    );
  }

  if (hideDownloadButton) {
    return null;
  }

  return (
    <Button
      type="text"
      size="small"
      className="campaign-review-btn-download"
      icon={<DownloadInvoiceIcon />}
      onClick={(e) => {
        e.stopPropagation();
        if (!assets?.length) return;
        downloadAll(assets, `${folderName}.zip`, folderId);
      }}
    />
  );
});
